import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { SharePrompt } from "@/components/SharePrompt";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("paid");
        } else if (tries >= 8) {
          setState("pending");
        } else {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId, tries]);

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen items-center justify-center bg-obsidian px-5 pt-24">
      <div className="w-full max-w-lg border border-white/10 bg-surface p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-neon-green" />
            <h1 className="mt-6 font-heading text-3xl uppercase text-white">Confirming Payment…</h1>
            <p className="mt-2 font-body text-sm text-white/60">Hang tight, this only takes a moment.</p>
          </>
        )}
        {(state === "paid" || state === "pending") && (
          <>
            <CheckCircle2 size={52} strokeWidth={2.5} className="mx-auto text-neon-green" />
            <h1 className="mt-6 font-heading text-4xl uppercase text-white">Payment {state === "paid" ? "Confirmed" : "Received"}!</h1>
            <p className="mt-3 font-body text-sm text-white/70">
              Thank you! Your payment to ARD Midsouth Performance is {state === "paid" ? "complete" : "processing"}. Our staff will follow up with next steps.
            </p>
            <Link to="/" data-testid="success-home-btn" className="mt-8 inline-block bg-neon-green px-8 py-4 font-heading text-sm uppercase tracking-widest text-black">Back To Home</Link>
            <SharePrompt />
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={52} strokeWidth={2.5} className="mx-auto text-red-400" />
            <h1 className="mt-6 font-heading text-3xl uppercase text-white">Something Went Wrong</h1>
            <p className="mt-3 font-body text-sm text-white/70">We couldn't confirm this payment. Please contact us and we'll help sort it out.</p>
            <Link to="/contact" className="mt-8 inline-block border border-white/20 px-8 py-4 font-heading text-sm uppercase tracking-widest text-white hover:border-neon-green hover:text-neon-green">Contact Us</Link>
          </>
        )}
      </div>
    </div>
  );
}
